import React from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Cookie,
  Shield,
  Eye,
  Database,
  Settings,
  Users,
  CheckCircle,
  AlertCircle,
  Clock,
  FileText,
  Mail,
  Phone,
} from "lucide-react";
import LegalPageShell, { LegalFeatureCard } from "../components/legal/LegalPageShell";
import { glassCard } from "../utils/glassStyles";

export default function CookiePolicy() {
  const cookieTypes = [
    {
      type: "Essential Cookies",
      icon: Shield,
      description: "Required for basic site functionality",
      examples: ["Session management", "Authentication", "Security"],
      duration: "Session-based or up to 24 hours",
      mandatory: true,
    },
    {
      type: "Functional Cookies",
      icon: Settings,
      description: "Remember your preferences and settings",
      examples: ["Language selection", "Theme preferences", "Region settings"],
      duration: "30 days to 1 year",
      mandatory: false,
    },
    {
      type: "Analytics Cookies",
      icon: Database,
      description: "Help us understand how visitors use our site",
      examples: ["Page visits", "Time spent", "Navigation paths"],
      duration: "24 hours to 2 years",
      mandatory: false,
    },
    {
      type: "Marketing Cookies",
      icon: Users,
      description: "Track visitors across websites for targeted advertising",
      examples: ["Ad personalization", "Campaign tracking", "Social media integration"],
      duration: "90 days to 2 years",
      mandatory: false,
    },
  ];

  const cookieFeatures = [
    {
      icon: CheckCircle,
      title: "No Third-Party Sharing",
      description: "We don't sell or share cookie data with third parties",
    },
    {
      icon: Eye,
      title: "Transparent Tracking",
      description: "Full visibility into what data we collect",
    },
    {
      icon: Shield,
      title: "GDPR Compliant",
      description: "Fully compliant with data protection regulations",
    },
    {
      icon: Clock,
      title: "Clear Expiration",
      description: "All cookies have defined expiration periods",
    },
  ];

  const cookieManagement = [
    {
      browser: "Google Chrome",
      link: "https://support.google.com/chrome/answer/95647",
      steps: "Settings → Privacy and security → Cookies and other site data",
    },
    {
      browser: "Mozilla Firefox",
      link: "https://support.mozilla.org/en-US/kb/enhanced-tracking-protection-firefox-desktop",
      steps: "Options → Privacy & Security → Cookies and Site Data",
    },
    {
      browser: "Safari",
      link: "https://support.apple.com/guide/safari/manage-cookies-and-website-data-sfri11471/mac",
      steps: "Preferences → Privacy → Cookies and website data",
    },
    {
      browser: "Microsoft Edge",
      link: "https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09",
      steps: "Settings → Cookies and site permissions → Cookies and site data",
    },
  ];

  return (
    <LegalPageShell
      title="Cookie Policy"
      subtitle="Understanding how we use cookies to enhance your experience on Shebeka."
      icon={Cookie}
      badges={[
        { label: "GDPR Compliant", className: "bg-green-500/10 text-green-700 dark:text-green-300" },
        { label: "No Data Selling", className: "bg-blue-500/10 text-blue-700 dark:text-blue-300" },
        { label: "Cookie Consent Required", className: "bg-accent/10 text-primary dark:text-accent" },
      ]}
      relatedLinks={[
        { to: "/privacy", label: "Privacy Policy" },
        { to: "/terms", label: "Terms of Service" },
        { to: "/contact", label: "Contact Privacy Team" },
      ]}
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        {cookieFeatures.map((feature) => (
          <LegalFeatureCard key={feature.title} {...feature} />
        ))}
      </div>

      <Card className={`mb-8 sm:mb-12 border-primary/20 ${glassCard}`}>
        <CardContent className="p-4 sm:p-6 md:p-8">
          <div className="flex flex-col sm:flex-row items-start gap-4">
            <FileText className="w-6 h-6 sm:w-8 sm:h-8 text-primary flex-shrink-0" />
            <p className="text-sm sm:text-base md:text-lg text-foreground leading-relaxed">
              This Cookie Policy explains how{" "}
              <span className="font-bold text-primary">Shebeka</span> uses cookies and similar
              technologies to recognize you when you visit our platform.
            </p>
          </div>
        </CardContent>
      </Card>

      <section className="mb-8 sm:mb-12">
        <h2 className="flex items-center gap-3 text-2xl sm:text-3xl font-bold text-foreground mb-6">
          <Cookie className="w-6 h-6 sm:w-7 sm:h-7 text-primary" />
          What Are Cookies?
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
          <Card className={glassCard}>
            <CardHeader>
              <CardTitle className="text-lg sm:text-xl">Definition</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm sm:text-base text-muted-foreground">
                Cookies are small text files placed on your device when you visit websites. They
                help websites work efficiently and provide information to site owners.
              </p>
            </CardContent>
          </Card>
          <Card className="bg-primary/5 border-primary/20">
            <CardHeader>
              <CardTitle className="text-lg sm:text-xl">Why We Use Them</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {[
                  "To enable basic website functionality",
                  "To remember your preferences and settings",
                  "To analyze how visitors use our platform",
                  "To improve user experience and platform performance",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2 text-sm text-foreground">
                    <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>
      </section>

      <section className="mb-8 sm:mb-12">
        <h2 className="flex items-center gap-3 text-2xl sm:text-3xl font-bold text-foreground mb-6">
          <Database className="w-6 h-6 sm:w-7 sm:h-7 text-primary" />
          Types of Cookies We Use
        </h2>
        <div className="space-y-4 sm:space-y-6">
          {cookieTypes.map((cookie) => {
            const Icon = cookie.icon;
            return (
              <Card
                key={cookie.type}
                className={`${glassCard} ${
                  cookie.mandatory
                    ? "border-green-200 dark:border-green-800/50"
                    : ""
                }`}
              >
                <CardContent className="p-4 sm:p-6">
                  <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                    <div
                      className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                        cookie.mandatory ? "bg-green-100" : "bg-primary/10"
                      }`}
                    >
                      <Icon
                        className={`w-6 h-6 ${
                          cookie.mandatory
                            ? "text-green-600 dark:text-green-400"
                            : "text-primary"
                        }`}
                      />
                    </div>
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <h3 className="text-lg font-bold">{cookie.type}</h3>
                        {cookie.mandatory && (
                          <span className="px-2 py-0.5 bg-green-100 text-green-800 dark:text-green-300 rounded-full text-xs font-bold">
                            ESSENTIAL
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground mb-4">{cookie.description}</p>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div className="rounded-lg p-3 bg-muted/30">
                          <h4 className="font-semibold text-sm mb-2">Examples</h4>
                          <ul className="space-y-1">
                            {cookie.examples.map((example) => (
                              <li key={example} className="text-xs text-muted-foreground">
                                {example}
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div className="rounded-lg p-3 bg-muted/30">
                          <h4 className="font-semibold text-sm mb-2">Duration</h4>
                          <p className="text-xs text-muted-foreground">{cookie.duration}</p>
                        </div>
                        <div className="rounded-lg p-3 bg-muted/30">
                          <h4 className="font-semibold text-sm mb-2">Status</h4>
                          <p className="text-xs text-muted-foreground">
                            {cookie.mandatory
                              ? "Cannot be disabled"
                              : "Optional - can be disabled"}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </section>

      <section className="mb-8 sm:mb-12">
        <h2 className="flex items-center gap-3 text-2xl sm:text-3xl font-bold text-foreground mb-6">
          <Settings className="w-6 h-6 sm:w-7 sm:h-7 text-primary" />
          Managing Your Cookie Preferences
        </h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
          <Card className={glassCard}>
            <CardHeader>
              <CardTitle className="text-lg sm:text-xl">Browser Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {cookieManagement.map((browser) => (
                <div key={browser.browser} className="rounded-lg p-3 bg-muted/30">
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
                    <span className="font-medium text-sm">{browser.browser}</span>
                    <a
                      href={browser.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-primary hover:underline"
                    >
                      View Guide
                    </a>
                  </div>
                  <p className="text-xs text-muted-foreground">{browser.steps}</p>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card className={glassCard}>
            <CardHeader>
              <CardTitle className="text-lg sm:text-xl">Our Cookie Consent Tool</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                You can manage cookie preferences through our built-in consent tool. Look for the
                cookie icon in the bottom-left corner of our website.
              </p>
              <div className="bg-yellow-500/10 rounded-lg p-4 border border-yellow-200 dark:border-yellow-800/30">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0" />
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    Disabling certain cookies may limit functionality. Essential cookies cannot be
                    disabled as they are required for the platform to function properly.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      <section className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 mb-8">
        <Card className={glassCard}>
          <CardHeader>
            <CardTitle className="text-lg sm:text-xl">Policy Updates</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs sm:text-sm text-muted-foreground mb-2">
              Last updated: January 4, 2025
            </p>
            <p className="text-xs sm:text-sm text-muted-foreground">
              We may update this Cookie Policy from time to time. Significant changes will be
              communicated through email or platform notifications.
            </p>
          </CardContent>
        </Card>

        <Card className={glassCard}>
          <CardHeader>
            <CardTitle className="text-lg sm:text-xl">Contact Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center gap-3">
              <Mail className="w-5 h-5 text-primary" />
              <span className="text-sm">privacy@shebeka.com</span>
            </div>
            <div className="flex items-center gap-3">
              <Phone className="w-5 h-5 text-primary" />
              <span className="text-sm">+251 96 233 3331</span>
            </div>
          </CardContent>
        </Card>
      </section>

      <Card className="bg-gradient-to-r from-primary to-accent border-0 text-white">
        <CardContent className="p-6 sm:p-8">
          <h3 className="text-lg sm:text-xl font-bold mb-2">Your Privacy Matters</h3>
          <p className="text-sm text-white/90">
            We are committed to transparency about how we use cookies and similar technologies.
          </p>
        </CardContent>
      </Card>
    </LegalPageShell>
  );
}
